// src/hooks/useAppUpdate.js
// Проверка новой версии: сравнивает вшитую в бандл версию с /version.json на сервере.
// Обе берутся из REACT_APP_BUILD_VERSION на одном шаге сборки (см. scripts/write-version.js).
import { useCallback, useEffect, useRef, useState } from 'react';

const BUNDLED_VERSION = process.env.REACT_APP_BUILD_VERSION || '';
const FIRST_CHECK_DELAY_MS = 5000;
const CHECK_INTERVAL_MS = 10 * 60 * 1000;

async function fetchServerVersion() {
  const response = await fetch('/version.json', { cache: 'no-store' });
  if (!response.ok) return null;
  const data = await response.json();
  return typeof data?.version === 'string' ? data.version : null;
}

export function useAppUpdate() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const foundRef = useRef(false);

  useEffect(() => {
    // Локальный `npm start`: версии нет — проверять не с чем
    if (!BUNDLED_VERSION) return undefined;

    let intervalId = null;

    const check = async () => {
      if (foundRef.current) return;
      try {
        const serverVersion = await fetchServerVersion();
        if (serverVersion && serverVersion !== BUNDLED_VERSION) {
          foundRef.current = true;
          setUpdateAvailable(true);
          clearInterval(intervalId); // дальше не опрашиваем
        }
      } catch {
        // сеть недоступна — попробуем в следующий раз
      }
    };

    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      // Закрытую плашку показываем снова при возврате во вкладку
      if (foundRef.current) setDismissed(false);
      else check();
    };

    const timeoutId = setTimeout(check, FIRST_CHECK_DELAY_MS);
    intervalId = setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const dismiss = useCallback(() => setDismissed(true), []);

  return { updateAvailable: updateAvailable && !dismissed, dismiss };
}
